import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { filter, map, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

import type { UserIdMap } from '@core/account-data';
import { AttachmentsActions } from '@core/attachments-data';
import type { Attachment } from '@core/attachments-types';
import { filterBoolean } from '@core/common-legacy';
import type { Requisition, RequisitionAssignedFlow } from '@core/requisitions-types';
import { RequisitionStatus } from '@core/requisitions-types';
import type { ApprovalRequestPayload, RequisitionUpdateData } from '@recruitee/requisitions-data';
import {
  AssignedFlowsActions,
  AssignedFlowsSelectors,
  RequisitionsActions,
  UpdateContext,
} from '@recruitee/requisitions-data';

import { RequisitionsDialogsService } from './requisitions-dialogs.service';
import { RequisitionsAnalytics } from '../analytics/requisitions.analytics';
import { RequisitionsDataProviderService } from '../data/single-requisition/requisitions-data-provider.service';

@Injectable()
export class SingleRequisitionsActionsService {
  private readonly requisitionsDialogs = inject(RequisitionsDialogsService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  private readonly requisitionsData = inject(RequisitionsDataProviderService);
  private readonly requisitionsAnalytics = inject(RequisitionsAnalytics);

  private requisition: Requisition;
  private adminsMap: UserIdMap;
  private isDepartmentsMode: boolean;
  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const requisitionsData = this.requisitionsData;

    requisitionsData?.data$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(({ requisition, users, isDepartmentsMode }) => {
        this.requisition = requisition;
        this.adminsMap = users;
        this.isDepartmentsMode = isDepartmentsMode;
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public save(data: RequisitionUpdateData, context: UpdateContext = UpdateContext.Default) {
    let confirm$: Observable<boolean>;
    if (this.isDepartmentsMode && data.departmentId !== this.requisition.departmentId) {
      confirm$ = this.requisitionsDialogs.confirmChangeDepartment$(context).pipe(take(1));
    } else {
      confirm$ = of(true);
    }

    return confirm$.pipe(
      tap(confirmed => {
        if (confirmed) {
          this.store.dispatch(
            RequisitionsActions.updateOnePessimistic({
              payload: { data, context },
            }),
          );
        }
      }),
    );
  }

  public deleteOpenings(data: RequisitionUpdateData, openingsDeleted: number): void {
    this.store.dispatch(
      RequisitionsActions.deleteOpenings({
        payload: { data, openingsDeleted },
      }),
    );
  }

  public unassignCandidate(openingId: number, requisitionId: number): void {
    this.requisitionsDialogs
      .confirmUnassignCandidate$()
      .pipe(take(1), filterBoolean)
      .subscribe(() => {
        this.store.dispatch(
          RequisitionsActions.unassignCandidate({
            payload: { openingId, requisitionId },
          }),
        );
      });
  }

  public requestApproval(requisition: Requisition) {
    if (!requisition.approvalFlowId) return;

    this.resolveFlow$(requisition).subscribe(flow =>
      this.requisitionsDialogs.openApprovalRequest$({
        submitHandler: (data: ApprovalRequestPayload) =>
          this.requestApprovalSubmitHandler$(requisition, data),
        uploadAttachmentHandler$: (file: File, uuid: string) =>
          this.uploadAttachmentHandler$(file, uuid),
        deleteAttachmentHandler$: (attachmentId: number) =>
          this.deleteAttachmentHandler$(attachmentId),
        approvalFlow: flow,
        adminsMap: this.adminsMap,
        approvalRequestData: requisition.approvalRequest,
        departmentName: requisition.department?.name,
        isDepartmentsMode: this.isDepartmentsMode,
      }),
    );
  }

  public approveRequest(
    requisitionId: number,
    approverDecisionId: number,
    currentStep: number,
  ): void {
    this.requisitionsDialogs
      .confirmApproveRequest$()
      .pipe(
        filterBoolean,
        tap(() => {
          this.store.dispatch(
            AssignedFlowsActions.approveRequest({
              payload: { requisitionId, approverDecisionId },
            }),
          );
        }),
        switchMap(() =>
          this.actions$.pipe(
            ofType(AssignedFlowsActions.approveRequestSuccess),
            take(1),
            tap(() => {
              this.requisitionsAnalytics.trackApprove(requisitionId, currentStep);
            }),
          ),
        ),
        switchMap(() =>
          this.actions$.pipe(
            ofType(RequisitionsActions.fetchOneSuccess),
            take(1),
            tap(({ payload }) => {
              const isRequisitionApproved = payload.data.status === RequisitionStatus.Approved;
              if (isRequisitionApproved) this.requisitionsAnalytics.trackApproved(requisitionId);
            }),
          ),
        ),
      )
      .subscribe();
  }

  public rejectRequest(
    requisitionId: number,
    approverDecisionId: number,
    currentStep: number,
  ): void {
    this.requisitionsDialogs
      .confirmRejectRequest$()
      .pipe(
        filterBoolean,
        tap(reason => {
          this.store.dispatch(
            AssignedFlowsActions.rejectRequest({
              payload: { requisitionId, approverDecisionId, reason },
            }),
          );
        }),
        switchMap(() =>
          this.actions$.pipe(
            ofType(AssignedFlowsActions.rejectRequestSuccess),
            take(1),
            tap(() => {
              this.requisitionsAnalytics.trackReject(requisitionId, currentStep);
            }),
          ),
        ),
      )
      .subscribe();
  }

  private requestApprovalSubmitHandler$(
    { id, status: fromStatus }: Requisition,
    data: ApprovalRequestPayload,
  ): Observable<boolean> {
    this.store.dispatch(
      RequisitionsActions.requestApproval({
        payload: {
          data: {
            id,
            status: RequisitionStatus.Pending,
            approvalRequest: data,
          },
        },
      }),
    );

    return this.actions$.pipe(
      ofType(RequisitionsActions.requestApprovalSuccess),
      take(1),
      tap(({ payload }) => {
        this.requisitionsAnalytics.trackRequestApproval(
          payload.data.id,
          fromStatus,
          !!payload.data.dynamicFields?.length,
        );
      }),
      switchMap(() => of(true)),
    );
  }

  private uploadAttachmentHandler$(file: File, uuid: string): Observable<Attachment> {
    this.store.dispatch(
      AttachmentsActions.uploadAttachmentPessimistic({
        payload: { file, correlationId: uuid },
      }),
    );

    return this.actions$.pipe(
      ofType(AttachmentsActions.uploadAttachmentPessimisticSuccess),
      filter(action => action.payload.correlationId === uuid),
      take(1),
      takeUntil(this.ngOnDestroy$.asObservable()),
      map(({ payload }) => payload.data),
    );
  }

  private deleteAttachmentHandler$(attachmentId: number): Observable<boolean> {
    this.store.dispatch(AttachmentsActions.deleteAttachment({ payload: attachmentId }));

    return this.actions$.pipe(
      ofType(AttachmentsActions.deleteAttachmentSuccess),
      take(1),
      takeUntil(this.ngOnDestroy$.asObservable()),
      switchMap(() => of(true)),
    );
  }

  private resolveFlow$(requisition: Requisition): Observable<RequisitionAssignedFlow> {
    const { id: requisitionId } = requisition;
    this.store.dispatch(AssignedFlowsActions.fetch({ payload: { requisitionId } }));

    return this.store
      .select(AssignedFlowsSelectors.byRequisitionId({ requisitionId }))
      .pipe(filterBoolean, take(1));
  }
}
